import { safeReturnTo } from '@/auth/config';
import { parseProfile } from '@/auth/profile';
import { profileQuery } from '@/auth/profile-query';
import type { OperatorSession } from '@/auth/session';
import {
  Avatar,
  AvatarFallback,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  SidebarMenuButton,
  useSidebar,
} from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { ChevronsUpDownIcon, LogOutIcon } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

/**
 * 사이드바 하단 사용자 메뉴 — 로그인한 계정을 보여 주고, 로그아웃은 메뉴 안에서 한 번 더 확인받는다.
 * 확인창은 메뉴 밖에 둔다 — 메뉴가 닫히며 같이 사라지지 않게.
 */
export function UserMenu({ session }: { session: OperatorSession }) {
  const { isMobile, setOpenMobile } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = useQuery(profileQuery);
  const profile = parseProfile(data);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const title = profile.name ?? profile.email ?? '내 계정';
  const initial = title.slice(0, 1).toUpperCase();

  const logout = () => {
    setOpenMobile(false);
    session.logout();
    let returnTo = '/properties';
    try {
      returnTo = safeReturnTo(`${location.pathname}${location.search}`);
    } catch {
      // 데모 화면은 로그인 뒤 돌아갈 곳이 아니다.
    }
    void navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
      replace: true,
      state: { from: 'session-expiry' },
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<SidebarMenuButton size="lg" tooltip={title} aria-label="사용자 메뉴" />}
        >
          <Avatar size="sm">
            <AvatarFallback>{initial}</AvatarFallback>
          </Avatar>
          <span className="grid flex-1 text-left leading-tight">
            <span className="label truncate">{title}</span>
            {profile.name && profile.email && (
              <span className="caption text-muted-foreground truncate">{profile.email}</span>
            )}
          </span>
          <ChevronsUpDownIcon className="ml-auto" />
        </DropdownMenuTrigger>
        <DropdownMenuContent side={isMobile ? 'bottom' : 'right'} align="end" sideOffset={8}>
          <DropdownMenuLabel>{profile.email ?? title}</DropdownMenuLabel>
          <DropdownMenuItem
            variant="destructive"
            onClick={() => {
              setConfirmOpen(true);
            }}
          >
            <LogOutIcon />
            로그아웃
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={logout}
        target="현재 계정"
        action="로그아웃"
        consequence="저장하지 않은 변경 사항은 사라집니다."
      />
    </>
  );
}
